/**
 * FmReportPageTyped — the report archetype shell.
 *
 * The binding route API is `chapters: FmReportChapterDescriptor[]`. Every
 * chapter owns navigation and owner-facing metadata and composes sections:
 * arrangements placing typed elements.
 */

import React from 'react';
import {
  FmSectionBand,
  FmReportNav,
  type FmReportNavProps,
} from './FmReportComposition';
import { FmReportChapter } from './FmReportChapter';
import { validateReportChapters } from './FmReportChapter.validation';
import { chapterNavLabel } from './FmReportChapter.types';
import type {
  FmReportChapterAssistantTools,
  FmReportChapterDescriptor,
} from './FmReportChapter.types';
import {
  FmReportWorkflows,
  type FmReportWorkflowDescriptor,
} from './FmReportPageTyped.workflows';
import { useSectionAlignment } from './FmSectionAlignment';
import FmWorkspaceShell from '@/shared/ui/FmWorkspaceShell';

export type {
  FmReportMessageWorkflowDescriptor,
  FmReportWorkflowDescriptor,
} from './FmReportPageTyped.workflows';
export type {
  FmSection as FmSectionDescriptor,
  FmSectionState,
  FmSectionTone,
  FmSectionWidth,
} from './sections/section.types';
export type * from './elements/elements.types';
export type * from './elements/elementFields.types';
export type * from './elements/elementMedia.types';
export type {
  FmReportChapterSectionDescriptor,
  FmReportChapterSectionRole,
  FmReportChapterDescriptor,
  FmReportChapterTone,
} from './FmReportChapter.types';

export interface FmReportPageTypedProps {
  /** Origin-aware return action owned by the report navigation. */
  backAction?: FmReportNavProps['backAction'];
  /** Page title (rendered in compact section band above nav). */
  title?: string;
  /** Page eyebrow/label. */
  eyebrow?: string;
  /**
   * Owner-facing chapters. Each chapter owns one navigation target and
   * composes ordered sections inside one semantic band.
   */
  chapters: FmReportChapterDescriptor[];
  /** Typed report Assistant boundary; required whenever a chapter declares Assistant context. */
  assistantTools?: FmReportChapterAssistantTools;
  /**
   * Section jump-nav mode. 'auto' (default) renders the shell FmReportNav from the
   * section list; 'none' suppresses it — for reports whose sections carry their own
   * docked sub-nav (e.g. the forest-composition section).
   */
  nav?: 'auto' | 'none';
  /** Navigation theme: light (default) or dark. */
  navTone?: 'light' | 'dark';
  /** Navigation variant: surface (default) or docked. */
  navVariant?: 'surface' | 'docked';
  /** Active section id (controls nav highlight). */
  activeSection?: string;
  /** Called when user selects a section in the nav. */
  onSectionChange?: (id: string) => void;
  /** Localized label for the canonical chapter navigation. */
  navAriaLabel?: string;
  /**
   * A report that participates in a dense owner workflow may use the canonical
   * workspace scroll owner. The archetype, never the route, owns that wrapper.
   */
  presentation?: 'report' | 'workspace';
  /** Stable boundary ids for the archetype-owned workspace presentation. */
  workspaceTestIds?: {
    root?: string;
    header?: string;
    content?: string;
  };
  /** Controlled, descriptor-only workflows opened from report actions. */
  workflows?: readonly FmReportWorkflowDescriptor[];
  /** ARIA label for the main element. */
  ariaLabel?: string;
  /** Test id. */
  'data-testid'?: string;
}

function validateReportPageDescriptors(
  chapters: FmReportChapterDescriptor[],
  assistantTools: FmReportChapterAssistantTools | undefined,
): void {
  validateReportChapters(chapters);
  if (chapters.some(({ recipe }) => recipe.assistant) && !assistantTools) {
    throw new Error('FmReportPageTyped requires assistantTools when a chapter declares recipe.assistant.');
  }
}

type ReportNavItem = {
  id: string;
  label: string;
  shortLabel: string | undefined;
  meta: undefined;
};

function buildReportNavItems(chapters: FmReportChapterDescriptor[]): ReportNavItem[] {
  return chapters.map((chapter) => ({
    id: chapter.id,
    label: chapterNavLabel(chapter),
    shortLabel: chapter.navShortLabel,
    meta: undefined,
  }));
}

function reportTitleBand(
  title: string | undefined,
  eyebrow: string | undefined,
  dataTestId: string,
): React.ReactNode {
  if (!title && !eyebrow) return null;
  return (
    <FmSectionBand
      tone="light"
      density="compact"
      width="content"
      eyebrow={eyebrow}
      title={title}
      titleLevel={1}
      className="fm-report-page__title"
      data-testid={`${dataTestId}-title`}
    />
  );
}

function reportNavigation(
  props: Pick<
    FmReportPageTypedProps,
    | 'activeSection'
    | 'backAction'
    | 'nav'
    | 'navAriaLabel'
    | 'navTone'
    | 'navVariant'
    | 'onSectionChange'
  >,
  items: ReportNavItem[],
  dataTestId: string,
): React.ReactNode {
  if (props.nav === 'none' || items.length === 0) return null;
  return (
    <FmReportNav
      backAction={props.backAction}
      items={items}
      activeId={props.activeSection}
      tone={props.navTone}
      variant={props.navVariant}
      onSelect={props.onSectionChange}
      navigationBehavior="controlled"
      ariaLabel={props.navAriaLabel}
      data-testid={`${dataTestId}-nav`}
    />
  );
}

/**
 * FmReportPageTyped — typed report archetype.
 *
 * Report routes pass ONLY: `FmReportChapterDescriptor[]`, semantic header/nav
 * props, and callbacks. Each chapter owns the owner question, literal answer,
 * dominant beat, transition, mobile order, conversion role, and ordered
 * sections. The archetype OWNS layout rhythm and descriptor → renderer mapping.
 * NO-FALLBACK: an invalid descriptor is a compile-time error.
 */
export const FmReportPageTyped: React.FC<FmReportPageTypedProps> = ({
  backAction,
  title,
  eyebrow,
  chapters,
  assistantTools,
  nav = 'auto',
  navTone = 'light',
  navVariant = 'surface',
  activeSection,
  onSectionChange,
  navAriaLabel,
  presentation = 'report',
  workspaceTestIds,
  workflows = [],
  ariaLabel,
  'data-testid': dataTestId = 'fm-report-page-typed',
}) => {
  validateReportPageDescriptors(chapters, assistantTools);

  const navItems = buildReportNavItems(chapters);
  const navItemIds = navItems.map(({ id }) => id).join('|');
  const chapterStateRevision = chapters
    .map((chapter) => [
      chapter.id,
      chapter.sections
        .map(({ section }) => `${section.id}:${section.state.status}`)
        .join(','),
    ].join(':'))
    .join('|');
  const getNavigation = React.useCallback(() =>
    document.querySelector<HTMLElement>(`[data-testid="${dataTestId}-nav"]`), [dataTestId]);
  const getSections = React.useCallback(() =>
    document.querySelector<HTMLElement>(`[data-testid="${dataTestId}-sections"]`), [dataTestId]);
  const alignReportSection = useSectionAlignment({
    getNavigation, getSections, revision: chapterStateRevision, enabled: true,
  });
  const lastRequestedHashRef = React.useRef<string | null>(null);
  const handleSectionChange = React.useCallback((id: string): void => {
    onSectionChange?.(id);
    window.history.pushState(null, '', `#${id}`);
    lastRequestedHashRef.current = id;
    window.requestAnimationFrame(() => alignReportSection(id, 'smooth'));
  }, [alignReportSection, onSectionChange]);

  // Only the OWNER asks for a chapter: the first paint carrying a hash, a
  // hashchange, or a nav click (handleSectionChange, above). This effect used
  // to re-run on `chapterStateRevision` and re-request the SAME hash on every
  // chapter status change, which took the page back from a reader who had
  // scrolled away to read -- an internal re-arm no input listener can defend
  // against. Keeping the requested chapter in place while the report grows is
  // the alignment hook's job now, so the revision no longer re-requests.
  React.useEffect(() => {
    let frame = 0;
    const scrollFromHash = (): void => {
      const sectionId = window.location.hash.replace(/^#/, '');
      if (!sectionId || !navItemIds.split('|').includes(sectionId)) return;
      if (lastRequestedHashRef.current === sectionId) return;
      lastRequestedHashRef.current = sectionId;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() =>
        alignReportSection(sectionId, 'auto'));
    };
    scrollFromHash();
    window.addEventListener('hashchange', scrollFromHash);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('hashchange', scrollFromHash);
    };
  }, [alignReportSection, navItemIds]);

  const report = (
    <>
      <main className="fm-report-page" data-testid={dataTestId} aria-label={ariaLabel}>
        {reportTitleBand(title, eyebrow, dataTestId)}
        {reportNavigation({
          activeSection,
          backAction,
          nav,
          navAriaLabel,
          navTone,
          navVariant,
          onSectionChange: handleSectionChange,
        }, navItems, dataTestId)}

        <div className="fm-report-page__sections" data-testid={`${dataTestId}-sections`}>
          {chapters.map((chapter, index) => (
            <FmReportChapter
              key={chapter.id}
              chapter={chapter}
              assistantTools={assistantTools}
              ownsPageHeading={!title && index === 0}
            />
          ))}
        </div>
      </main>
      <FmReportWorkflows workflows={workflows} />
    </>
  );

  if (presentation === 'workspace') {
    return (
      <FmWorkspaceShell
        rootTestId={workspaceTestIds?.root}
        headerTestId={workspaceTestIds?.header}
        contentTestId={workspaceTestIds?.content}
      >
        {report}
      </FmWorkspaceShell>
    );
  }

  return report;
};

FmReportPageTyped.displayName = 'FmReportPageTyped';
